import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import CommerciumLoaderHost from 'src/utils/CommerciumLoaderHost'
import { toast } from 'react-toastify'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import {
  getPlatformChildFieldDataCategoriesAsync,
  getSelectedCategoryTreeAsync,
  getFieldTreeSearchAsync,
  refreshPlatformFieldDataAttributesAsync,
} from 'src/redux/actions/templateAndPricingActions'
import { getPlatformFieldsAsync } from 'src/redux/actions/productActions'
import { AttributeType, CATEGORY_ITEM_ON_UPDATE, Category } from 'src/interfaces/Template.interface'
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import { buildFieldTree, FlatFieldTreeItem } from 'src/utils/buildFieldTree'
import { useAttributePolling } from 'src/hooks/useAttributePolling'
import { useProgressiveLoadingMessage } from 'src/marketConnect/helpers'
import InputAttributes from './InputAttributes'
import SectionHeaderInfoTooltip, { LISTING_PROFILE_TOOLTIPS } from './SectionHeaderInfoTooltip'
import {
  normalizeCategoryPlatformFieldsPayload,
  resolveCategoryPlatformFieldId,
} from './utils/listingProfileCategoryFieldUtils'
import { readFieldTreeThunkResult } from './utils/fieldTreeResponse'
import {
  resolveSuggestionCategoryId,
  shouldSkipCategoryAttributeCatalogFetch,
} from './utils/categorySuggestionAttributes'

export interface CategoryMappingChangeData {
  categoryId: number | null
  categoryPath: string
  isLeafCategory: boolean
  showAttributes: boolean
  isAttributeLoading: boolean
}

export interface CategoryMappingEngineProps {
  selectedSecondaryPlatform: PlatformDataType | null
  isEditMode?: boolean
  initialCategoryId?: number | null
  initialCategoryPath?: string
  initialPlatformFieldId?: number | null
  updatingMapData?: CATEGORY_ITEM_ON_UPDATE | null
  inputAttributesKey?: string
  onCategoryChange?: (data: CategoryMappingChangeData) => void
  onAttributeSelection: (attributes: any[]) => void
  nativeFields?: any[]
  customFields?: any[]
  /** Presentation-only skin for listing profile create/edit accordion */
  appearance?: 'default' | 'listingProfile'
  /** AI category suggestion attribute_field rows — skips catalog GET when category matches */
  preloadedCategoryAttributes?: AttributeType[]
}

const TREE_PAGE_LIMIT = 30
const TREE_SCROLL_MAX_DEFAULT = 320
const TREE_SCROLL_MIN = 120
const TREE_DROPDOWN_VIEWPORT_MARGIN_PX = 20

export const MANUAL_MAP_PLATFORMS = [
  'DEBENHAMS',
  'SUPERPHARM',
  'CATCH',
  'THERANGEPLUS',
  'VINTED',
  'EBAY',
  'ETSY',
  'TIKTOK',
  'BACKMARKET',
  'POSHMARK',
  'TEMU',
  'LAZADA',
  'VESTIAIRECOLLECTIVE',
  'CDISCOUNT',
  'SHOPIFY',
  'SHOPWARE',
  'BIGCOMMERCE',
  'OnBuy',
  'SHEIN',
  'ZALORA',
  'WHATNOT',
  'JUMIAVENDOR',
  'KOGAN',
  'KOGANNZD',
  'TARGETPLUS',
  'FAIRE',
  'FRUUGO',
  'FRUUGOV2',
  'WALMART',
  'WIX',
  'GUNBROKER',
]

const CategoryMappingEngine: FC<CategoryMappingEngineProps> = ({
  selectedSecondaryPlatform,
  isEditMode = false,
  initialCategoryId = null,
  initialCategoryPath = '',
  initialPlatformFieldId = null,
  updatingMapData = null,
  inputAttributesKey,
  onCategoryChange,
  onAttributeSelection,
  nativeFields,
  customFields,
  appearance = 'default',
  preloadedCategoryAttributes = [],
}) => {
  const isListingProfile = appearance === 'listingProfile'
  type TreeNode = import('src/utils/buildFieldTree').FieldTreeNode

  const dispatch = useAppDispatch()
  const { getAttributeData } = useAppSelector((state) => state.templateAndPricing)

  const {
    isAttributeLoading,
    showAttributes,
    setShowAttributes,
    fetchAttributesForCategory,
    clearPolling,
  } = useAttributePolling(selectedSecondaryPlatform)

  const forceCatalogFetchRef = useRef(false)
  const [, setForceCatalogFetchTick] = useState(0)

  const preloadedCatalogCategoryId =
    resolveSuggestionCategoryId(updatingMapData?.category_data) ??
    (initialCategoryId != null ? Number(initialCategoryId) : null)

  const categoryHasPreloadedAttributes = (categoryId: number | null): boolean =>
    shouldSkipCategoryAttributeCatalogFetch({
      forceCatalog: forceCatalogFetchRef.current,
      preloadedAttributes: preloadedCategoryAttributes,
      selectedCategoryId: categoryId,
      savedCategoryId: preloadedCatalogCategoryId,
    })

  const loadAttributesForCategory = useCallback(
    (categoryId: number, options?: { forceCatalog?: boolean }) => {
      if (options?.forceCatalog) {
        forceCatalogFetchRef.current = true
        setForceCatalogFetchTick((n) => n + 1)
        fetchAttributesForCategory(categoryId)
        return
      }
      if (categoryHasPreloadedAttributes(categoryId)) {
        clearPolling()
        setShowAttributes(true)
        return
      }
      fetchAttributesForCategory(categoryId)
    },
    [
      preloadedCategoryAttributes,
      preloadedCatalogCategoryId,
      fetchAttributesForCategory,
      clearPolling,
      setShowAttributes,
    ]
  )

  const attributeLoadingMessage = useProgressiveLoadingMessage(isAttributeLoading)
  const needsManualMapButton = MANUAL_MAP_PLATFORMS.includes(
    selectedSecondaryPlatform?.code || ''
  )
  const selectedPlatformCode = (
    selectedSecondaryPlatform?.code ||
    selectedSecondaryPlatform?.name ||
    selectedSecondaryPlatform?.platform_info?.name ||
    selectedSecondaryPlatform?.display_name ||
    ''
  ).toUpperCase()
  const isCdiscountPlatform = selectedPlatformCode === 'CDISCOUNT'


  const [categoryMainId, setCategoryMainId] = useState<number | null>(null)
  const [isLeafCategory, setIsLeafCategory] = useState(false)
  const [selectedCategoryIdForMapping, setSelectedCategoryIdForMapping] = useState<number | null>(
    null
  )
  const [treeData, setTreeData] = useState<TreeNode[]>([])
  const [treeSearch, setTreeSearch] = useState('')
  const [treeSelectedId, setTreeSelectedId] = useState<number | null>(null)
  const [treeSelectedPath, setTreeSelectedPath] = useState<string>('')
  const [isTreeOpen, setIsTreeOpen] = useState(false)
  const [treeSearchResults, setTreeSearchResults] = useState<Category[]>([])
  const [treeSearchLoading, setTreeSearchLoading] = useState(false)
  const [treeSearchPage, setTreeSearchPage] = useState(1)
  const [treeSearchHasMore, setTreeSearchHasMore] = useState(false)
  const [treeSearchLoadingMore, setTreeSearchLoadingMore] = useState(false)
  const [treePage, setTreePage] = useState(1)
  const [treeHasMore, setTreeHasMore] = useState(true)
  const [treeLoadingMore, setTreeLoadingMore] = useState(false)
  const [refreshAttributesLoading, setRefreshAttributesLoading] = useState(false)
  const [refreshAttributesError, setRefreshAttributesError] = useState<string>('')
  const [categoryTreeBootstrapError, setCategoryTreeBootstrapError] = useState('')
  const [categoryTreeLoadError, setCategoryTreeLoadError] = useState('')
  const [treeSearchError, setTreeSearchError] = useState('')
  const [selectedCategoryAttributeSetId, setSelectedCategoryAttributeSetId] = useState<
    string | number | boolean | null | undefined
  >(undefined)


  const treeDropdownRef = useRef<HTMLDivElement | null>(null)
  const treeTriggerRef = useRef<HTMLDivElement | null>(null)
  const treeDropdownSearchRef = useRef<HTMLDivElement | null>(null)
  const [listingProfileTreeScrollMaxHeight, setListingProfileTreeScrollMaxHeight] =
    useState(TREE_SCROLL_MAX_DEFAULT)
  const treeSearchDebounce = useRef<NodeJS.Timeout | null>(null)
  const flatTreeResultsRef = useRef<FlatFieldTreeItem[]>([])
  const treeSearchRef = useRef('')
  const onCategoryChangeRef = useRef(onCategoryChange)
  const editHydratedRef = useRef(false)
  const rootTreePlatformIdRef = useRef<number | null>(null)
  const treeLoadGenRef = useRef(0)
  const treeSearchGenRef = useRef(0)
  const initialPlatformFieldIdRef = useRef(initialPlatformFieldId)
  initialPlatformFieldIdRef.current = initialPlatformFieldId
  rootTreePlatformIdRef.current = selectedSecondaryPlatform?.id ?? null

  // ── style helpers ──
  const treeContainerStyle: React.CSSProperties = {
    border: '1px solid #d0d7de',
    borderRadius: 8,
    padding: 8,
    boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
    background: '#fff',
  }

  const nodeRowStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '4px 6px',
    borderRadius: 4,
    cursor: 'pointer',
    backgroundColor: isSelected ? '#e7f1ff' : 'transparent',
  })

  // tree helpers

  const updateNode = (
    nodes: TreeNode[],
    id: number,
    updater: (n: TreeNode) => TreeNode
  ): TreeNode[] =>
    nodes.map((n) =>
      n.id === id
        ? updater(n)
        : { ...n, children: n.children ? updateNode(n.children, id, updater) : n.children }
    )

  const findNodeById = (nodes: TreeNode[], id: number): TreeNode | undefined => {
    for (const n of nodes) {
      if (n.id === id) return n
      if (n.children?.length) {
        const found = findNodeById(n.children, id)
        if (found) return found
      }
    }
    return undefined
  }

  const mergeUniqueById = <T extends { id: number }>(currentItems: T[], nextItems: T[]) =>
    [...currentItems, ...nextItems].filter(
      (item, idx, arr) => arr.findIndex((candidate) => candidate.id === item.id) === idx
    )

  const renderHighlightedText = (text: string, term?: string) => {
    const trimmedTerm = term?.trim()
    if (!trimmedTerm) return text

    const escapedTerm = trimmedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escapedTerm})`, 'ig'))
    const lowerTerm = trimmedTerm.toLowerCase()

    return parts.map((part, index) =>
      part.toLowerCase() === lowerTerm ? (
        <mark key={`${part}-${index}`} className='px-1 py-0 rounded bg-warning bg-opacity-25'>
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    )
  }

  const loadTree = async (
    platformId: number,
    platformFieldId: number,
    opts?: { selectedChildId?: number; searchTerm?: string; page?: number }
  ) => {
    const page = opts?.page ?? 1
    const requestGen = ++treeLoadGenRef.current
    setCategoryTreeLoadError('')
    const res: any = await dispatch(
      getFieldTreeSearchAsync({
        categoryId: platformId,
        platform_field_id: platformFieldId,
        search_string: opts?.searchTerm ?? '',
        page,
        limit: TREE_PAGE_LIMIT,
        use_pagination: true,
        selected_child_id: opts?.selectedChildId,
      })
    )
    if (treeLoadGenRef.current !== requestGen) return
    const pageData = readFieldTreeThunkResult(res)
    if (!pageData) {
      setCategoryTreeLoadError("Couldn't load categories.")
      return
    }
    if (pageData.salvagedFromError) {
      console.warn(
        'get-field-tree returned 400 with category results; using the response body'
      )
    }
    const results = pageData.rows as FlatFieldTreeItem[]
    const merged =
      page === 1 ? results : mergeUniqueById(flatTreeResultsRef.current, results)
    flatTreeResultsRef.current = merged
    setTreeData(buildFieldTree(merged))
    setTreePage(page)
    setTreeHasMore(Boolean(pageData.next))
  }

  const loadSearchTree = async (
    platformId: number,
    platformFieldId: number,
    searchTerm: string,
    page = 1
  ) => {
    const requestGen = ++treeSearchGenRef.current
    setTreeSearchError('')
    const res: any = await dispatch(
      getFieldTreeSearchAsync({
        categoryId: platformId,
        platform_field_id: platformFieldId,
        search_string: searchTerm,
        page,
        limit: TREE_PAGE_LIMIT,
        use_pagination: true,
      })
    )

    if (
      treeSearchGenRef.current !== requestGen ||
      treeSearchRef.current.trim() !== searchTerm
    ) {
      return
    }

    const pageData = readFieldTreeThunkResult(res)
    if (!pageData) {
      setTreeSearchError("Couldn't search categories.")
      return
    }
    const results = pageData.rows as Category[]
    setTreeSearchResults((prev) =>
      page === 1 ? results : mergeUniqueById(prev, results)
    )
    setTreeSearchPage(page)
    setTreeSearchHasMore(Boolean(pageData.next))
  }

  const handleTreeScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (target.scrollTop + target.clientHeight < target.scrollHeight - 20) return

    const platformId = selectedSecondaryPlatform?.id
    if (!platformId || !categoryMainId) return

    const searchTerm = treeSearch.trim()
    const isSearchActive = searchTerm.length >= 2

    if (isSearchActive) {
      if (!treeSearchHasMore || treeSearchLoading || treeSearchLoadingMore) return
      setTreeSearchLoadingMore(true)
      try {
        await loadSearchTree(platformId, categoryMainId, searchTerm, treeSearchPage + 1)
      } finally {
        setTreeSearchLoadingMore(false)
      }
      return
    }

    if (!treeHasMore || treeLoadingMore) return
    setTreeLoadingMore(true)
    try {
      await loadTree(platformId, categoryMainId, { page: treePage + 1 })
    } finally {
      setTreeLoadingMore(false)
    }
  }

  const loadChildren = async (nodeId: number) => {
    if (!categoryMainId) return []
    setTreeData((prev) => updateNode(prev, nodeId, (n) => ({ ...n, loading: true })))
    const res: any = await dispatch(
      getPlatformChildFieldDataCategoriesAsync({
        platform_Id: nodeId,
        search_string: '',
        page: 1,
        limit: 10,
        use_pagination: true,
        platform_field_id: categoryMainId,
      })
    )
    const kids: TreeNode[] = res?.payload?.results
      ? res.payload.results.map((k: Category) => ({ ...k, children: [], hasFetched: false }))
      : []
    setTreeData((prev) =>
      updateNode(prev, nodeId, (n) => ({
        ...n,
        loading: false,
        isExpanded: true,
        hasFetched: true,
        children: kids,
      }))
    )
    return kids
  }

  const buildTreeFromSearchResults = (results: any[]): TreeNode[] => {
    let syntheticId = -1
    const roots: TreeNode[] = []

    const findOrCreate = (
      list: TreeNode[],
      label: string,
      parentId: number | null,
      baseData?: any,
      isLeafOverride?: boolean
    ): TreeNode => {
      let node = list.find(
        (n) => n.api_name === label && (parentId === null || n.parent_data === parentId)
      )
      if (!node) {
        node = {
          id: baseData?.id ?? syntheticId--,
          api_name: label,
          api_id: baseData?.api_id ?? '',
          api_code: baseData?.api_code ?? '',
          attribute_set_id: baseData?.attribute_set_id,
          updated_at: baseData?.updated_at ?? '',
          created_at: baseData?.created_at ?? '',
          platform: baseData?.platform ?? selectedSecondaryPlatform?.id ?? 0,
          platform_field: baseData?.platform_field ?? categoryMainId ?? 0,
          parent_data: baseData?.parent_data ?? parentId,
          child_data: [],
          children: [],
          hasFetched: true,
          isExpanded: true,
          loading: false,
          isLeaf: Boolean(isLeafOverride),
        }
        list.push(node)
      }
      node.isExpanded = true
      return node
    }

    results.forEach((item) => {
      const parts = item?.api_name
        ? item.api_name
          .split('->')
          .map((p: string) => p.trim())
          .filter(Boolean)
        : []
      if (!parts.length) return
      let currentList = roots
      let parentId: number | null = null
      parts.forEach((label: string, idx: number) => {
        const isLeaf = idx === parts.length - 1
        const baseData = isLeaf ? item : undefined
        const node = findOrCreate(currentList, label, parentId, baseData, isLeaf)
        if (isLeaf) {
          node.id = item.id
          node.api_id = item.api_id ?? node.api_id
          node.api_code = item.api_code ?? node.api_code
          node.attribute_set_id = item.attribute_set_id
          node.parent_data = item.parent_data ?? node.parent_data
          node.isLeaf = true
        }
        if (!node.children) node.children = []
        currentList = node.children
        parentId = node.id
      })
    })

    return roots
  }

  const buildTreeFromPathNodes = (pathNodes: Category[]): TreeNode[] => {
    const roots: TreeNode[] = []
    let prevNode: TreeNode | null = null
    pathNodes.forEach((p, idx) => {
      const node: TreeNode = {
        ...p,
        children: [],
        hasFetched: true,
        isExpanded: true,
        loading: false,
        isLeaf: idx === pathNodes.length - 1,
      }
      if (idx === 0) {
        roots.push(node)
      } else if (prevNode) {
        prevNode.children = prevNode.children || []
        prevNode.children.push(node)
      }
      prevNode = node
    })
    return roots
  }

  const reloadFullCategoryTree = () => {
    const platformId = selectedSecondaryPlatform?.id
    if (!platformId || !categoryMainId) {
      setTreeData(buildFieldTree(flatTreeResultsRef.current))
      return
    }
    setTreePage(1)
    setTreeHasMore(true)
    loadTree(platformId, categoryMainId, { page: 1 })
  }

  const handleClearCategory = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    forceCatalogFetchRef.current = false
    setSelectedCategoryIdForMapping(null)
    setSelectedCategoryAttributeSetId(null)
    setTreeSelectedPath('')
    setTreeSelectedId(null)
    setShowAttributes(false)
    setIsLeafCategory(false)
    setTreeSearch('')
    onAttributeSelection([])
    reloadFullCategoryTree()
  }

  const handleTreeSelect = async (node: TreeNode) => {
    if (node.id !== selectedCategoryIdForMapping) {
      onAttributeSelection([])
    }
    setTreeSelectedId(node.id)
    setShowAttributes(false)
    setIsLeafCategory(false)
    setSelectedCategoryIdForMapping(node.id)
    setSelectedCategoryAttributeSetId(node.attribute_set_id ?? node.item?.attribute_set_id)

    try {
      const pathRes: any = await dispatch(
        getSelectedCategoryTreeAsync({ category_data_id: node.id })
      )
      const path: Category[] = pathRes?.payload || []
      if (path.length) {
        setTreeSelectedPath(path.map((p) => p.api_name).join(' > '))
        setTreeData(buildTreeFromPathNodes(path))
      } else if (node.api_name) {
        setTreeSelectedPath(node.api_name)
      }
    } catch (err) {
      if (node.api_name) setTreeSelectedPath(node.api_name)
    }

    const children = node.hasFetched ? node.children || [] : await loadChildren(node.id)
    if (!children || children.length === 0) {
      setIsLeafCategory(true)
      loadAttributesForCategory(node.id)
    }
    setTreeSearch('')
    setIsTreeOpen(false)
  }

  const handleNodeClick = async (node: TreeNode) => {
    if (node.loading) return
    if (node.children && node.children.length > 0) {
      setTreeData((prev) =>
        updateNode(prev, node.id, (n) => ({
          ...n,
          isExpanded: !n.isExpanded,
          children: n.children || [],
          isLeaf: false,
        }))
      )
      return
    }

    if (!node.hasFetched) {
      const kids = await loadChildren(node.id)
      if (kids.length) {
        setTreeData((prev) =>
          updateNode(prev, node.id, (n) => ({
            ...n,
            isExpanded: true,
            hasFetched: true,
            children: kids as any,
            isLeaf: false,
          }))
        )
        return
      }
    }

    await handleTreeSelect(node)
  }

  const renderTree = (nodes: TreeNode[], depth: number, term?: string) => {
    const list =
      term
        ? buildTreeFromSearchResults(treeSearchResults)
        : nodes

    const attachState = (items: TreeNode[]): TreeNode[] =>
      items.map((n) => {
        const stateNode = findNodeById(treeData, n.id)
        const merged = stateNode ? { ...n, ...stateNode } : n
        return { ...merged, children: n.children ? attachState(n.children) : [] }
      })

    const viewList = term ? list : attachState(list)

    const renderNodes = (items: TreeNode[], level: number): JSX.Element[] =>
      items.map((node) => (
        <div key={node.id} className='mb-1'>
          <div className='d-flex align-items-center'>
            {Array.from({ length: level }).map((_, idx) => (
              <span
                key={idx}
                style={{
                  width: 14,
                  borderLeft: '1px solid #d0d7de',
                  marginRight: 6,
                  height: '100%',
                }}
              ></span>
            ))}
            <Button
              variant='outline-secondary'
              size='sm'
              className='p-0 me-2'
              type='button'
              style={{ width: 22, height: 22, lineHeight: '20px', fontSize: 12 }}
              onClick={async () => {
                if (node.loading) return
                if (node.hasFetched && node.children && node.children.length > 0) {
                  setTreeData((prev) =>
                    updateNode(prev, node.id, (n) => ({ ...n, isExpanded: !n.isExpanded }))
                  )
                  return
                }
                if (!node.hasFetched) {
                  const kids = await loadChildren(node.id)
                  if ((kids as any)?.length) {
                    setTreeData((prev) =>
                      updateNode(prev, node.id, (n) => ({
                        ...n,
                        children: kids as any,
                        hasFetched: true,
                        isExpanded: true,
                        isLeaf: false,
                      }))
                    )
                  }
                  return
                }
              }}
            >
              {node.loading
                ? '...'
                : node.children && node.children.length
                  ? node.isExpanded
                    ? '-'
                    : '+'
                  : node.hasFetched && (node.children?.length ?? 0) === 0
                    ? '-'
                    : '+'}
            </Button>
            <span
              role='button'
              className={`flex-grow-1 ${treeSelectedId === node.id ? 'fw-semibold text-primary' : ''
                }`}
              style={nodeRowStyle(treeSelectedId === node.id)}
              onClick={() => handleNodeClick(node)}
            >
              {renderHighlightedText(node.api_name, term)}
            </span>
          </div>
          {node.isExpanded && node.children?.length ? (
            <div style={{ width: '100%' }}>{renderNodes(node.children, level + 1)}</div>
          ) : null}
        </div>
      ))

    return renderNodes(viewList, depth)
  }

  // ── utility ──

  function isErrorWithMessage(
    data: AttributeType[] | { message: string; queue_id?: string }
  ): data is { message: string; queue_id?: string } {
    const candidate = data as { message?: string; queue_id?: string }
    return Boolean(candidate?.message && !candidate?.queue_id)
  }

  const handleAttributeSelection = (attributes: any[]) => {
    onAttributeSelection(attributes)
  }

  const shouldShowRefreshAttributesButton =
    isEditMode && selectedPlatformCode === 'SHEIN' && selectedCategoryIdForMapping !== null

  const variantProductMessage =
    selectedCategoryAttributeSetId === true ||
    String(selectedCategoryAttributeSetId ?? '').trim().toLowerCase() === 'true'
      ? 'Variant Products is allowed for this Category'
      : 'Variant Products is not allowed for this Category'

  const getRefreshAttributesErrorMessage = (errorData: any) => {
    const apiMessage =
      errorData?.massage ||
      errorData?.message ||
      errorData?.data?.massage ||
      errorData?.data?.message ||
      errorData?.error?.massage ||
      errorData?.error?.message

    return apiMessage
      ? `Unable to refresh attributes. ${apiMessage}`
      : 'Unable to refresh attributes.'
  }

  const handleRefreshAttributes = async () => {
    if (selectedCategoryIdForMapping === null) return
    setRefreshAttributesLoading(true)
    setRefreshAttributesError('')
    try {
      const response: any = await dispatch(
        refreshPlatformFieldDataAttributesAsync({
          platform_field_data_id: selectedCategoryIdForMapping,
          platform_id: selectedSecondaryPlatform?.id,
        })
      )
      if (response.meta?.requestStatus === 'rejected') {
        setRefreshAttributesError(
          getRefreshAttributesErrorMessage(response.payload || response.error)
        )
        return
      }
      toast.success('Attributes refresh started successfully')
      loadAttributesForCategory(selectedCategoryIdForMapping, { forceCatalog: true })
    } catch (error) {
      setRefreshAttributesError(getRefreshAttributesErrorMessage(error))
    } finally {
      setRefreshAttributesLoading(false)
    }
  }



  // Keep onCategoryChangeRef current
  useEffect(() => {
    onCategoryChangeRef.current = onCategoryChange
  }, [onCategoryChange])

  useEffect(() => {
    treeSearchRef.current = treeSearch.trim()
  }, [treeSearch])

  // Root category list: same empty-search first page as legacy, once the channel id is known.
  // Edit prop updates must not cancel this load.
  useEffect(() => {
    const platformId = selectedSecondaryPlatform?.id
    if (!platformId) return

    const requestPlatformId = platformId
    setCategoryTreeBootstrapError('')

    dispatch(
      getPlatformFieldsAsync({
        platformId,
        otherParams: { field_code: 'CATEGORY' },
      })
    ).then((response) => {
      if (rootTreePlatformIdRef.current !== requestPlatformId) return
      const fieldId = resolveCategoryPlatformFieldId(
        normalizeCategoryPlatformFieldsPayload(response?.payload),
        initialPlatformFieldIdRef.current
      )
      if (!fieldId) {
        setCategoryTreeBootstrapError('Unable to load categories for this channel.')
        return
      }
      setCategoryMainId(fieldId)
      setTreePage(1)
      setTreeHasMore(true)
      loadTree(platformId, fieldId, { page: 1 })
    })
  }, [selectedSecondaryPlatform?.id])

  // Saved category label and attributes. Does not replace the root list.
  useEffect(() => {
    if (!isEditMode || !initialCategoryId) return
    if (editHydratedRef.current) return
    editHydratedRef.current = true

    const categoryId = initialCategoryId
    let fieldId = initialPlatformFieldId ?? null
    const platformId = selectedSecondaryPlatform?.id

    const applySavedCategory = (resolvedFieldId: number | null) => {
      if (platformId != null && rootTreePlatformIdRef.current !== platformId) return
      if (resolvedFieldId) setCategoryMainId(resolvedFieldId)
      setTreeSelectedId(categoryId)
      setTreeSelectedPath(initialCategoryPath || '')
      setSelectedCategoryIdForMapping(categoryId)
      setSelectedCategoryAttributeSetId(
        (updatingMapData?.category_data as any)?.attribute_set_id
      )
      loadAttributesForCategory(categoryId)
    }

    if (fieldId || !platformId) {
      applySavedCategory(fieldId)
      return
    }

    dispatch(
      getPlatformFieldsAsync({
        platformId,
        otherParams: { field_code: 'CATEGORY' },
      })
    ).then((response: any) => {
      fieldId = resolveCategoryPlatformFieldId(
        normalizeCategoryPlatformFieldsPayload(response?.payload),
        null
      )
      applySavedCategory(fieldId)
    })
  }, [isEditMode, initialCategoryId, initialPlatformFieldId, selectedSecondaryPlatform?.id])

  const updateListingProfileTreeScrollHeight = useCallback(() => {
    if (!isListingProfile || !isTreeOpen) return
    const trigger = treeTriggerRef.current
    if (!trigger) return
    const triggerBottom = trigger.getBoundingClientRect().bottom
    const searchHeight =
      treeDropdownSearchRef.current?.getBoundingClientRect().height ?? 56
    const viewportHeight =
      typeof window !== 'undefined'
        ? window.visualViewport?.height ?? window.innerHeight
        : TREE_SCROLL_MAX_DEFAULT
    const available =
      viewportHeight -
      triggerBottom -
      TREE_DROPDOWN_VIEWPORT_MARGIN_PX -
      searchHeight
    const next = Math.max(
      TREE_SCROLL_MIN,
      Math.min(TREE_SCROLL_MAX_DEFAULT, Math.floor(available))
    )
    setListingProfileTreeScrollMaxHeight(next)
  }, [isListingProfile, isTreeOpen])

  useLayoutEffect(() => {
    if (!isListingProfile || !isTreeOpen) {
      setListingProfileTreeScrollMaxHeight(TREE_SCROLL_MAX_DEFAULT)
      return
    }
    updateListingProfileTreeScrollHeight()
    window.addEventListener('resize', updateListingProfileTreeScrollHeight)
    window.addEventListener('scroll', updateListingProfileTreeScrollHeight, true)
    window.visualViewport?.addEventListener(
      'resize',
      updateListingProfileTreeScrollHeight
    )
    return () => {
      window.removeEventListener('resize', updateListingProfileTreeScrollHeight)
      window.removeEventListener('scroll', updateListingProfileTreeScrollHeight, true)
      window.visualViewport?.removeEventListener(
        'resize',
        updateListingProfileTreeScrollHeight
      )
    }
  }, [isListingProfile, isTreeOpen, updateListingProfileTreeScrollHeight])

  // Click outside closes tree dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        treeDropdownRef.current &&
        !treeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsTreeOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced tree search
  useEffect(() => {
    if (treeSearchDebounce.current) clearTimeout(treeSearchDebounce.current)
    const searchTerm = treeSearch.trim()
    if (!searchTerm || searchTerm.length < 2) {
      setTreeSearchResults([])
      setTreeSearchLoading(false)
      setTreeSearchError('')
      setTreeSearchPage(1)
      setTreeSearchHasMore(false)
      setTreeSearchLoadingMore(false)
      return
    }
    const searchRootId = selectedSecondaryPlatform?.id
    if (!searchRootId || !categoryMainId) {
      setTreeSearchLoading(false)
      return
    }
    treeSearchDebounce.current = setTimeout(() => {
      setTreeSearchLoading(true)
      setTreeSearchResults([])
      setTreeSearchPage(1)
      setTreeSearchHasMore(false)
      setTreeSearchLoadingMore(false)
      loadSearchTree(searchRootId, categoryMainId, searchTerm, 1)
        .finally(() => setTreeSearchLoading(false))
    }, 500)
    return () => {
      if (treeSearchDebounce.current) clearTimeout(treeSearchDebounce.current)
    }
  }, [treeSearch, categoryMainId, selectedSecondaryPlatform?.id, dispatch])

  // Notify parent when relevant state changes
  useEffect(() => {
    onCategoryChangeRef.current?.({
      categoryId: selectedCategoryIdForMapping,
      categoryPath: treeSelectedPath,
      isLeafCategory,
      showAttributes,
      isAttributeLoading,
    })
  }, [
    selectedCategoryIdForMapping,
    treeSelectedPath,
    isLeafCategory,
    showAttributes,
    isAttributeLoading,
  ])

  // When AI catalog is present for the selected leaf, show attributes without catalog polling.
  useEffect(() => {
    if (selectedCategoryIdForMapping == null || !isLeafCategory) return
    if (!categoryHasPreloadedAttributes(selectedCategoryIdForMapping)) return
    clearPolling()
    setShowAttributes(true)
  }, [
    preloadedCategoryAttributes,
    selectedCategoryIdForMapping,
    isLeafCategory,
    preloadedCatalogCategoryId,
    clearPolling,
    setShowAttributes,
  ])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      clearPolling()
    }
  }, [])

  const savedCategoryId =
    initialCategoryId ??
    (typeof updatingMapData?.category_data === 'object'
      ? (updatingMapData.category_data as any)?.id
      : updatingMapData?.category_data)
  const shouldUseSavedAttributeMappings =
    Boolean(updatingMapData) &&
    selectedCategoryIdForMapping != null &&
    savedCategoryId != null &&
    Number(selectedCategoryIdForMapping) === Number(savedCategoryId)

  const usePreloadedAttributeCatalog = categoryHasPreloadedAttributes(
    selectedCategoryIdForMapping
  )

  const rootClass = isListingProfile ? 'product-listing__category-engine' : undefined

  const treePicker = (
      <div
        className={isListingProfile ? 'product-listing__category-engine__chain-block' : 'mb-8'}
        ref={treeDropdownRef}
      >
        <label
          className={
            isListingProfile
              ? 'product-listing__category-engine__subheading'
              : 'form-label'
          }
        >
          Select Category
        </label>
        {categoryTreeBootstrapError ? (
          <div
            className={
              isListingProfile
                ? 'product-listing__category-engine__helper text-danger mb-2'
                : 'form-text text-danger mb-2'
            }
          >
            {categoryTreeBootstrapError}
          </div>
        ) : null}
        {categoryTreeLoadError ? (
          <div
            className={
              isListingProfile
                ? 'product-listing__category-engine__helper text-danger mb-2'
                : 'form-text text-danger mb-2'
            }
          >
            {categoryTreeLoadError}{' '}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={reloadFullCategoryTree}
            >
              Retry
            </button>
          </div>
        ) : null}
        <div className='position-relative'>
          <div
            ref={treeTriggerRef}
            className={
              isListingProfile
                ? 'product-listing__category-engine__select-trigger d-flex align-items-center justify-content-between'
                : 'form-control d-flex align-items-center justify-content-between'
            }
            role='button'
            onClick={() => {
              setIsTreeOpen((prev) => !prev)
            }}
            onFocus={() => {
              setIsTreeOpen(true)
            }}
          >
            <span
              className={
                treeSelectedPath
                  ? isListingProfile
                    ? 'product-listing__category-engine__select-value'
                    : ''
                  : 'text-muted'
              }
            >
              {treeSelectedPath || 'Search categories'}
            </span>
            <div className='d-flex align-items-center'>
              {treeSelectedPath && (
                <i
                  className='bi bi-x-circle me-2 text-muted'
                  role='button'
                  title='Clear selection'
                  style={{ fontSize: '0.9rem' }}
                  onClick={handleClearCategory}
                />
              )}
              <i className='bi bi-search me-2 text-muted'></i>
              <i
                className={`bi ${isTreeOpen ? 'bi-chevron-up' : 'bi-chevron-down'
                  } text-muted`}
              ></i>
            </div>
          </div>

          {isTreeOpen && (
            <div
              className={
                isListingProfile
                  ? 'product-listing__category-engine__dropdown position-absolute w-100 bg-white mt-1'
                  : 'position-absolute w-100 bg-white mt-1'
              }
              style={
                isListingProfile
                  ? { zIndex: 10 }
                  : {
                      zIndex: 10,
                      border: '1px solid #d0d7de',
                      borderRadius: 8,
                      boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
                    }
              }
            >
              <div
                ref={isListingProfile ? treeDropdownSearchRef : undefined}
                className='p-2 border-bottom'
              >
                <div className='position-relative'>
                  <input
                    className={
                      isListingProfile
                        ? 'form-control product-listing__category-engine__search-input'
                        : 'form-control'
                    }
                    placeholder='Search categories'
                    value={treeSearch}
                    onChange={(e) => setTreeSearch(e.target.value)}
                    autoFocus
                  />
                  <i
                    className='bi bi-search position-absolute'
                    style={{
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6c757d',
                    }}
                  ></i>
                </div>
                {treeSearchLoading && treeSearchResults.length === 0 && (
                  <CommerciumLoaderHost
                    variant='inline'
                    layout='row'
                    compact
                    className='mt-2'
                    label={<span className='text-muted'>Searching…</span>}
                  />
                )}
              </div>
              <div
                style={{
                  maxHeight: isListingProfile
                    ? listingProfileTreeScrollMaxHeight
                    : TREE_SCROLL_MAX_DEFAULT,
                  overflow: 'auto',
                  ...treeContainerStyle,
                  border: 'none',
                  boxShadow: 'none',
                  borderRadius: '0 0 8px 8px',
                }}
                onScroll={handleTreeScroll}
              >
                {treeSearchError ? (
                  <div className='text-danger px-2 py-3'>
                    {treeSearchError}{' '}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => {
                        const searchTerm = treeSearch.trim()
                        const platformId = selectedSecondaryPlatform?.id
                        if (!platformId || !categoryMainId || searchTerm.length < 2) return
                        setTreeSearchLoading(true)
                        loadSearchTree(platformId, categoryMainId, searchTerm, 1).finally(() =>
                          setTreeSearchLoading(false)
                        )
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : null}
                {treeSearch.trim().length >= 2 &&
                  !treeSearchLoading &&
                  !treeSearchError &&
                  treeSearchResults.length === 0 ? (
                  <div className='text-muted px-2 py-3'>No results found</div>
                ) : null}
                {renderTree(
                  treeData,
                  0,
                  treeSearch.trim().length >= 2 ? treeSearch.trim() : undefined
                )}
                {(treeLoadingMore || treeSearchLoadingMore) && (
                  <div className='text-center py-2 text-muted small'>Loading more...</div>
                )}
              </div>
            </div>
          )}
        </div>
        {isCdiscountPlatform && selectedCategoryIdForMapping !== null && (
          <div
            className={
              isListingProfile
                ? 'product-listing__category-engine__helper form-text mt-2'
                : 'form-text mt-2'
            }
          >
            Note : {variantProductMessage}
          </div>
        )}
      </div>
  )

  const mapAttributesActions =
    needsManualMapButton || shouldShowRefreshAttributesButton ? (
      <div
        className={
          isListingProfile
            ? 'product-listing__category-engine__actions text-center'
            : 'm-3 text-center'
        }
      >
        <div className="d-inline-flex gap-2 align-items-center flex-wrap justify-content-center">
          {needsManualMapButton && (
            <Button
              type="button"
              variant="primary"
              className={isListingProfile ? 'product-listing__toolbar-btn fw-semibold' : undefined}
              size={isListingProfile ? 'sm' : undefined}
              disabled={selectedCategoryIdForMapping == null || isAttributeLoading}
              onClick={() =>
                loadAttributesForCategory(selectedCategoryIdForMapping!, { forceCatalog: true })
              }
            >
              {isListingProfile
                ? isAttributeLoading
                  ? 'Refreshing…'
                  : 'Refresh Attribute'
                : 'Map Attributes'}
            </Button>
          )}
          {shouldShowRefreshAttributesButton && (
            <Button
              type="button"
              variant="secondary"
              className={isListingProfile ? 'product-listing__toolbar-btn fw-semibold' : undefined}
              size={isListingProfile ? 'sm' : undefined}
              disabled={selectedCategoryIdForMapping == null || refreshAttributesLoading}
              onClick={handleRefreshAttributes}
            >
              {refreshAttributesLoading
                ? isListingProfile
                  ? 'Refreshing…'
                  : 'Refreshing...'
                : isListingProfile
                  ? 'Refresh Attribute'
                  : 'Refresh Attributes'}
            </Button>
          )}
          {isListingProfile &&
          (needsManualMapButton || shouldShowRefreshAttributesButton) ? (
            <SectionHeaderInfoTooltip
              id="listing-profile-refresh-attribute-tooltip"
              message={LISTING_PROFILE_TOOLTIPS.refreshAttribute}
            />
          ) : null}
        </div>
        {refreshAttributesError && (
          <div className='text-danger mt-3'>{refreshAttributesError}</div>
        )}
      </div>
    ) : null

  const attributesPanel = (
    <>
      {isAttributeLoading && !usePreloadedAttributeCatalog && (
        <div
          className={
            isListingProfile
              ? 'product-listing__category-engine__loading d-flex flex-column align-items-center text-center my-4'
              : 'd-flex flex-column align-items-center text-center my-4 text-secondary'
          }
        >
          <CommerciumLoaderHost variant="section" layout="centered" compact />
          {attributeLoadingMessage && (
            <div className={isListingProfile ? 'product-listing__category-engine__helper mt-3' : 'mt-3'}>
              {attributeLoadingMessage}
            </div>
          )}
        </div>
      )}

      {showAttributes &&
        (usePreloadedAttributeCatalog ? (
          <InputAttributes
            key={inputAttributesKey || String(selectedCategoryIdForMapping)}
            paltformId={selectedSecondaryPlatform?.id}
            categoryAttributes={preloadedCategoryAttributes}
            handleAttributeSelection={handleAttributeSelection}
            updatingMapData={shouldUseSavedAttributeMappings ? updatingMapData ?? null : null}
            nativeFields={nativeFields}
            customFields={customFields}
            layoutVariant={isListingProfile ? 'listingProfile' : 'default'}
          />
        ) : isErrorWithMessage(getAttributeData) ? (
          <div className='d-flex flex-column align-items-center text-center my-4 text-secondary'>
            <i className='bi bi-exclamation-circle fs-4 mb-1'></i>
            <div className='fw-semibold fs-5'>
              {getAttributeData?.message || 'Something went wrong.'}
            </div>
          </div>
        ) : (
          <InputAttributes
            key={inputAttributesKey || String(selectedCategoryIdForMapping)}
            paltformId={selectedSecondaryPlatform?.id}
            categoryAttributes={getAttributeData}
            handleAttributeSelection={handleAttributeSelection}
            updatingMapData={shouldUseSavedAttributeMappings ? updatingMapData ?? null : null}
            nativeFields={nativeFields}
            customFields={customFields}
            layoutVariant={isListingProfile ? 'listingProfile' : 'default'}
          />
        ))}
    </>
  )

  if (!isListingProfile) {
    return (
      <div>
        {treePicker}
        {mapAttributesActions}
        {attributesPanel}
      </div>
    )
  }

  const showAttributesSection =
    mapAttributesActions != null ||
    (isAttributeLoading && !usePreloadedAttributeCatalog) ||
    showAttributes ||
    usePreloadedAttributeCatalog

  return (
    <div className={rootClass}>
      {treePicker}
      {showAttributesSection ? (
        <div className='product-listing__category-engine__attributes-block'>
          <div className='product-listing__category-engine__divider' aria-hidden='true' />
          {mapAttributesActions}
          {attributesPanel}
        </div>
      ) : null}
    </div>
  )
}

export default CategoryMappingEngine
